// Новая партия: визуальная расстановка позиции, единая доска/шкала/тулбар,
// игра со Stockfish и автосохранение после каждого хода.
import { Chess } from 'chess.js';
import { MiniBoard } from '@ext/panel/board/mini-board';
import { winPct } from '@ext/core/evals';
import { t, currentLang } from '../i18n';
import { alertMsg, hideMainButton, showBackButton } from '../tg';
import { analyze } from '../services/engine-loader';
import { PriorityScheduler } from '../services/analysis';
import { PlayEngineController } from './play-engine';
import { BoardToolbar, type BoardHost } from './board-toolbar';
import { PositionEditor } from './position-editor';
import { pgnFromRecord, saveMyGame } from '../services/my-games';
import type { Recorded, PlayResult } from '../services/play-engine';

export function renderMyGamePlay(root: HTMLElement): void {
  const setupCopy = currentLang() === 'ru'
    ? 'Расставь фигуры на доске, выбери свой цвет и начинай игру.'
    : 'Set up the pieces on the board, choose your color and start playing.';
  root.className = 'app v2';
  root.innerHTML = `<header class="topbar"><span class="back" data-back>${t('top.back')}</span><span class="top-title"><span class="top-logo" aria-hidden="true"></span>${t('my.new')}</span><span>Stockfish</span></header>
    <section class="card my-setup" data-setup><h1>${t('my.setup')}</h1><p class="muted">${setupCopy}</p>
      <input class="my-name-input" data-name maxlength="64" placeholder="${t('my.new')}">
      <div data-position-editor></div>
      <div class="side-picker"><button class="inline-cta active" data-side="w">${t('my.white')}</button><button class="ghost-btn" data-side="b">${t('my.black')}</button></div>
      <button type="button" class="inline-cta my-start" data-start>${t('my.start_fen')}</button></section>
    <section class="hidden" data-game>
      <section class="review-board"><div class="eval-bar" data-eval><span class="black"></span><span class="white"></span></div><div class="board-stage" data-board></div></section>
      <div data-nav></div><div class="row between my-tools"><span class="muted tiny" data-save>${t('my.not_saved')}</span></div><div data-play-host></div></section>`;
  hideMainButton();
  const back = () => { location.hash = '#/train/my'; };
  showBackButton(back); root.querySelector('[data-back]')?.addEventListener('click', back);

  const setup = root.querySelector<HTMLElement>('[data-setup]')!;
  const game = root.querySelector<HTMLElement>('[data-game]')!;
  const editor = new PositionEditor();
  root.querySelector<HTMLElement>('[data-position-editor]')!.appendChild(editor.el);
  let side: 'w' | 'b' = 'w'; let startFen = new Chess().fen(); let title = '';
  let pendingSave: { record: Recorded[]; result: PlayResult } | null = null; let saveRunning = false;
  const gameId = `played_${Date.now()}`;
  const board = new MiniBoard(root.querySelector<HTMLElement>('[data-board]')!, { onMove: (san, fen) => play.handleUserMove(san, fen) });
  const sched = new PriorityScheduler(analyze);
  let orient: 'white' | 'black' = 'white';
  const bar = root.querySelector<HTMLElement>('[data-eval]')!;
  const paintEval = (cp: number | null) => {
    const white = cp == null ? 50 : winPct(cp); bar.style.setProperty('--white', String(white)); bar.style.setProperty('--black', String(100 - white));
  };

  const save = async (record: Recorded[], result: PlayResult) => {
    if (!record.length) return; pendingSave = { record: [...record], result };
    if (saveRunning) return; saveRunning = true;
    while (pendingSave) {
      const next = pendingSave; pendingSave = null;
      const pgn = pgnFromRecord(next.record, side, next.result, startFen === new Chess().fen() ? undefined : startFen);
      try {
        await saveMyGame({ id: gameId, date: new Date().toISOString(), side, result: next.result || '*', opponent: t('my.engine'), source: 'played', fen: startFen, title }, pgn);
        root.querySelector<HTMLElement>('[data-save]')!.textContent = t('my.saved');
      } catch (e) { alertMsg(t(e instanceof Error && e.message === 'limit' ? 'my.limit' : 'my.save_error')); }
    }
    saveRunning = false;
  };

  let toolbar: BoardToolbar | null = null;
  const play = new PlayEngineController({
    board, host: root.querySelector<HTMLElement>('[data-play-host]')!, analyze: (fen) => sched.requestPriority(fen),
    exitLabel: t('my.finish'), onExit: back, onRecord: save, onEval: paintEval,
    onHintBusy: (busy) => toolbar?.setHintBusy(busy),
  });
  const host: BoardHost = {
    first: () => {}, prev: () => {}, next: () => {}, last: () => {},
    navState: () => ({ atStart: true, atEnd: true }),
    flip: () => { orient = orient === 'white' ? 'black' : 'white'; board.setOrientation(orient); bar.classList.toggle('flipped', orient === 'black'); },
    applyBest: (show) => play.setShowBest(show), startPlay: () => play.start(startFen, side), play,
  };
  toolbar = new BoardToolbar(host);
  root.querySelector<HTMLElement>('[data-nav]')!.replaceWith(toolbar.el);

  root.querySelectorAll<HTMLElement>('[data-side]').forEach((el) => el.addEventListener('click', () => {
    side = el.dataset.side as 'w' | 'b';
    root.querySelectorAll('[data-side]').forEach((b) => b.classList.remove('active'));
    el.classList.add('active');
  }));
  root.querySelector('[data-start]')?.addEventListener('click', () => {
    const fen = `${editor.placement()} ${side} - - 0 1`;
    try { const chess = new Chess(); chess.load(fen, { skipValidation: false }); startFen = chess.fen(); }
    catch { return alertMsg(t('my.fen_error')); }
    title = root.querySelector<HTMLInputElement>('[data-name]')!.value.trim().slice(0, 64);
    orient = side === 'w' ? 'white' : 'black'; board.setOrientation(orient); bar.classList.toggle('flipped', orient === 'black');
    setup.classList.add('hidden'); game.classList.remove('hidden'); play.start(startFen, side); play.setShowBest(toolbar!.bestOn); toolbar!.refresh();
  });
}
